import {
  DASHBOARD_LINK,
  ROUTE_PAGE_SUBSCRIPTION,
} from '@components/Router/constants'
import { MessagesActions } from '@store/messagesSaga'
import { closeModal, openModal } from '@storeOld/reducers/ui'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { type GtmTriggerName, generateSubscriptionGtmId } from '@/constants/gtm'
import { isTeamOrAgencyPlan } from '@/constants/userSubscriptionPlans'
import type { AppState, AuthState } from '@/types'
import Api from '../../../api'
import {
  MODAL_PLAN_CHURN,
  MODAL_PLAN_DOWNGRADED,
  MODAL_PLAN_UPGRADE_SUCCESSFUL,
  MODAL_TEAM_UPGRADE_SUCCESSFUL,
} from '../Modal/constants'
import { planTypes } from '../Subscribe/Pricing/planTypes'

type MessageType =
  | 'plan_upgraded'
  | 'plan_downgraded'
  | 'plan_churn'
  | 'save_project'

const PLAN_MESSAGE_TYPES: MessageType[] = [
  'plan_upgraded',
  'plan_downgraded',
  'plan_churn',
]

const Messages = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { authenticated, id } = useSelector<AppState, AuthState>(
    ({ auth }) => auth
  )

  useEffect(() => {
    const abortController = new AbortController()
    const { signal } = abortController

    if (authenticated) {
      Api.Messages.get({ signal })
        .then((res) => res.json())
        .then((res) => {
          if (res.length) {
            res.forEach((message: any) => {
              if (PLAN_MESSAGE_TYPES.includes(message.type)) {
                const planId = message.metadata.to.id as string

                const gtagId = generateSubscriptionGtmId(
                  message.type === 'plan_upgraded',
                  planId
                ) as GtmTriggerName

                switch (message.type) {
                  case 'plan_upgraded': {
                    const upgradedProps = {
                      planId,
                      gtagId,
                      title: 'Upgrade successful!',
                      text: 'Congratulations on unlocking premium benefits!',
                      onViewed: () => {
                        dispatch(MessagesActions.viewed({ id: message.id }))
                      },
                    }

                    if (isTeamOrAgencyPlan(planId)) {
                      dispatch(
                        // @ts-expect-error
                        openModal(MODAL_TEAM_UPGRADE_SUCCESSFUL, {
                          ...upgradedProps,
                          buttonTitle: 'Set up your team',
                        })
                      )

                      break
                    }

                    dispatch(
                      // @ts-expect-error
                      openModal(MODAL_PLAN_UPGRADE_SUCCESSFUL, {
                        ...upgradedProps,
                        buttonTitle: 'Go to dashboard',
                        onButtonClick: () => {
                          navigate(DASHBOARD_LINK)
                          dispatch(closeModal())
                        },
                      })
                    )

                    break
                  }
                  case 'plan_downgraded': {
                    const currentPlan =
                      planTypes[planId as keyof typeof planTypes]

                    dispatch(
                      // @ts-expect-error
                      openModal(MODAL_PLAN_DOWNGRADED, {
                        planId,
                        gtagId,
                        title: 'Account downgraded',
                        text: `Your subscription plan has been changed to ${currentPlan.name}`,
                        buttonTitle: 'Go to dashboard',
                        onButtonClick: () => {
                          navigate(DASHBOARD_LINK)
                          dispatch(closeModal())
                        },
                        onViewed: () => {
                          dispatch(MessagesActions.viewed({ id: message.id }))
                        },
                      })
                    )

                    break
                  }
                  case 'plan_churn': {
                    dispatch(
                      // @ts-expect-error
                      openModal(MODAL_PLAN_CHURN, {
                        planId,
                        gtagId,
                        title: 'Account downgraded',
                        text: 'Your subscription has expired. We hope you enjoyed using our tool and look forward to your return in the future!',
                        buttonTitle: 'Subscription',
                        onButtonClick: () => {
                          navigate(ROUTE_PAGE_SUBSCRIPTION)
                          dispatch(closeModal())
                        },
                        onViewed: () => {
                          dispatch(MessagesActions.viewed({ id: message.id }))
                        },
                      })
                    )

                    break
                  }
                  default: {
                    break
                  }
                }
              }

              if (message.type === 'save_project') {
                // const projectId = message.metadata.id
                // @todo fix this
                // saveProject(
                //   dispatch,
                //   projectId,
                //   project,
                //   `${project.name} (Saved from Free Trial)`
                // )
              }
            })
          }
        })
        .catch((err) => {
          console.error(err)
        })
    }

    return () => {
      abortController.abort()
    }
  }, [authenticated, id])

  return null
}

export default Messages
